import { Box, Grid, InputBase, styled } from '@mui/material';
import { SearchIcon } from '@react-pdf-viewer/search';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ClientDTO, ClientContactIdsDto, getClientContactByClientIds } from 'wisestep-api';
import { WsButton, WsListingLoader, WsTypography, WsColors } from 'wisestep-base';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { StyledFooter } from '../../../features/jobs/assign/types';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { IClientCheckBoxListOptions } from '../helper/ClientSettingsHelper';

import { IOrganizationalUnitAllClientManagers } from './AllClientManagersInUnitList';
import ClientSpocs from './ClientsAndSpocsCheckbox';

interface IclientManagersList {
  allClientList: ClientDTO[];
  onInput?: (data: FieldValues) => void;
  savedContactIds?: string[];
  onUpdateHeader?: (data: string) => void;
}
type SelectedSpocs = {
  [clientId: string]: string[];
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  border: '2px solid transparent',
  backgroundColor: WsColors.TertiaryShades100,
  width: '29.5vw',
  padding: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ClientManagerList = ({
  allClientList,
  onInput,
  savedContactIds,
  onUpdateHeader,
}: IclientManagersList) => {
  const methods = useForm();
  const { watch, setValue } = methods;
  const [isLoading, setLoading] = useState<boolean>(true);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [clientsList, setClientsList] = useState<ClientDTO[]>([]);
  const [savedContactId, setSavedContactId] = useState<string[]>([]);
  const [clientContactList, setClientContactList] = useState<any[]>([]);
  const [filteredClientContacts, setFilteredClientContacts] = useState<any[]>([]);
  const [step, setStep] = useState(1);
  const selectedClient = watch('selectedClientsList');
  const selectedSpocs = watch('spocs');

  useEffect(() => {
    setClientsList(allClientList);
    setSavedContactId(savedContactIds || []);
  }, [allClientList, savedContactIds]);

  const fetchData = async (data: FieldValues) => {
    try {
      setLoading(true);
      const selectedIds = data?.selectedClientsList?.map((element) => element);
      const payload: ClientContactIdsDto = {
        ids: selectedIds,
      };
      const response = await getClientContactByClientIds(payload);
      const responseData: IOrganizationalUnitAllClientManagers[] = response?.data ?? [];
      const res = responseData.filter((item) => !savedContactId.includes(item.id ?? ''));
      const client = transformData(res);
      setFilteredClientContacts(client);
      setClientContactList(client);
    } catch (error) {
      setClientContactList([]);
    } finally {
      setLoading(false);
    }
  };

  const transformData = (managersData) => {
    const clientMap = {};

    managersData.forEach((manager) => {
      const clientId = manager.clientId;
      const clientName = manager.clientName;

      if (!clientMap[clientId]) {
        clientMap[clientId] = {
          id: clientId,
          name: clientName,
          spocs: [],
        };
      }

      clientMap[clientId].spocs.push({
        id: manager.id,
        name: `${manager.firstName} ${manager.lastName}`,
        status: manager.markStatus ? 'Active' : 'Inactive',
      });
    });

    return Object.values(clientMap);
  };

  const clientsOptions: IClientCheckBoxListOptions = useMemo(() => {
    const temp: IClientCheckBoxListOptions = {};
    clientsList?.forEach((client) => {
      if (client?.id) {
        temp[client.id] = {
          id: client?.id,
          name: client?.name ?? 'NA',
          status: client?.clientStatus?.isMarkInactive ? 'Inactive' : 'Active', // Provide default status if not available
        };
      }
    });
    return temp;
  }, [clientsList]);

  const handleSelectAllSpocs = (checked: boolean, clientId: string) => {
    const spocIds = clientContactList
      .find((client) => client.id === clientId)
      ?.spocs.map((spoc) => spoc.id);
    checked ? setValue(`spocs.${clientId}`, spocIds) : setValue(`spocs.${clientId}`, []);
  };

  const calculateTotalLength = (obj: SelectedSpocs) =>
    Object.values(obj).reduce((acc, list) => acc + list.length, 0);

  const gatherAllSelectedSpocIds = (selectedSpocs: SelectedSpocs): string[] =>
    Object.values(selectedSpocs).flat();

  const totalselected = selectedSpocs ? calculateTotalLength(selectedSpocs) : 0;

  const saveSelection = (data) => {
    if (Object.keys(data.spocs).some((key) => data.spocs[key].length > 0)) {
      const allSelectedSpocIds = gatherAllSelectedSpocIds(data.spocs);
      onInput && onInput(allSelectedSpocIds);
    } else {
      setNotification(warning('Please select at least one SPOC'));
    }
  };

  const handleProceed = (data) => {
    if (data?.selectedClientsList?.length > 0) {
      onUpdateHeader && onUpdateHeader('Step 2 : Select Client Spocs');
      fetchData(data);
      setStep(2);
    } else {
      setNotification(warning('Please select at least one client'));
    }
  };

  const handleSelectEvents = (checked: boolean) =>
    setValue('selectedClientsList', checked ? Object.keys(clientsOptions) : []);

  const handlePrevbutton = () => {
    setValue('spocs', {});
    setStep(1);
    onUpdateHeader && onUpdateHeader('Step 1: Select Clients');
  };

  const onClientsInput = useCallback(
    ({ target: { value } }) => {
      if (!isEmpty(value)) {
        const lowercasedValue = value.toLowerCase();
        const filteredRows =
          allClientList?.filter((row) => row?.name?.toLowerCase().includes(lowercasedValue)) ?? [];
        setClientsList(filteredRows);
      } else {
        setClientsList(allClientList);
      }
    },
    [allClientList]
  );

  const onClientContactsInput = useCallback(
    ({ target: { value } }) => {
      if (!isEmpty(value)) {
        const lowercasedValue = value.toLowerCase();
        const filteredRows = clientContactList
          .map((client) => ({
            ...client,
            spocs: client.spocs.filter((spoc) => spoc.name.toLowerCase().includes(lowercasedValue)),
          }))
          .filter((client) => client.spocs.length > 0);
        setFilteredClientContacts(filteredRows);
      } else {
        setFilteredClientContacts(clientContactList);
      }
    },
    [clientContactList]
  );

  return (
    <Box>
      <FormProvider {...methods}>
        {step === 1 ? (
          <>
            <Grid container alignItems="center" pl={2} spacing={1}>
              <SearchIcon />
              <Grid item>
                <InputBase
                  placeholder="search"
                  name="searchtext"
                  onChange={onClientsInput}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid spacing={1}>
              <Grid container pl={2}>
                <WsCheckBox
                  name="selectAll"
                  label="Select All Clients"
                  bold={true}
                  onClick={handleSelectEvents}
                />
              </Grid>
              <Grid container pl={2} pb={8.5}>
                <ClientSpocs name="selectedClientsList" clientsOptions={clientsOptions} />
              </Grid>
            </Grid>

            <StyledFooter>
              <Grid container item justifyContent="space-between" py={1}>
                <WsButton variant="text" size="large">
                  {`${selectedClient?.length ?? 0} Clients Selected`}
                </WsButton>
                <WsButton
                  size="large"
                  variant="contained"
                  onClick={methods.handleSubmit(handleProceed)}
                >
                  Proceed
                </WsButton>
              </Grid>
            </StyledFooter>
          </>
        ) : isLoading ? (
          <WsListingLoader />
        ) : (
          <>
            <Grid container alignItems="center" pl={2} spacing={1}>
              <SearchIcon />
              <Grid item>
                <InputBase
                  placeholder="search"
                  name="searchtext"
                  onChange={onClientContactsInput}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              {selectedClient.map((clientId) => {
                const client = filteredClientContacts.find((client) => client?.id === clientId);
                if (!client) return null;
                return (
                  <Grid item xs={12} spacing={1} pl={2} key={client.id} marginLeft={2}>
                    <WsTypography bold={true}>{`${client.name} (${
                      (selectedSpocs && selectedSpocs[client?.id]?.length) ?? 0
                    } of ${client.spocs.length})`}</WsTypography>
                    <WsCheckBox
                      name={`selectAllSpocs_${client.id}`}
                      label="Select All SPOCs"
                      bold={true}
                      onClick={(checked) => handleSelectAllSpocs(checked, client.id)}
                    />
                    <ClientSpocs clientId={client.id} spocs={client.spocs} />
                  </Grid>
                );
              })}
            </Grid>
            <StyledFooter>
              <StyledGrid container item spacing={1}>
                <Grid item sx={{ marginRight: '210px' }}>
                  <WsButton variant="text" size="large">
                    {`${totalselected ?? 0} Spocs Selected`}
                  </WsButton>
                </Grid>

                <WsButton variant="outlined" size="medium" onClick={handlePrevbutton} type="button">
                  Prev
                </WsButton>
                <WsButton
                  size="medium"
                  variant="contained"
                  onClick={methods.handleSubmit(saveSelection)}
                >
                  Save
                </WsButton>
              </StyledGrid>
            </StyledFooter>
          </>
        )}
      </FormProvider>
    </Box>
  );
};
export default ClientManagerList;
